import { useAuthStore } from '@/stores/auth';
import {
  getCommonStyles,
  getSizeClass
} from '@/utils/printHelpers';
import { createPreBillStyles, createReceiptStyles } from '@/utils/printStyles';
import {
  buildItemsHTML,
  createFooterSection,
  createHeaderSection,
  createItemsBlock,
  createMiddleBlock,
  createOrderInfoSection,
  createPaymentSection,
  createTotalsSection,
  type CustomerPrintOrder
} from '@/utils/customerPrintBuilders';
import { usePrint } from './usePrint';

export function useCustomerPrint() {
  const authStore = useAuthStore();
  const { isPrinting, printWithBuilder, electronPrint } = usePrint();

  const buildCustomerDocumentHTML = (
    order: CustomerPrintOrder,
    paperWidth: number,
    isPreBill: boolean
  ): string => {
    const sizeClass = getSizeClass(paperWidth);
    const commonStyles = getCommonStyles(paperWidth);
    const styles = isPreBill
      ? createPreBillStyles(paperWidth, commonStyles)
      : createReceiptStyles(paperWidth, commonStyles);
    const itemsHTML = buildItemsHTML(order, isPreBill);
    const headerHTML = createHeaderSection(authStore.restaurant);
    const orderInfoHTML = createOrderInfoSection(order, isPreBill);
    const totalsHTML = createTotalsSection(order, isPreBill);
    const paymentHTML = isPreBill ? '' : createPaymentSection(order);
    const footerHTML = createFooterSection(isPreBill);
    const itemsBlock = createItemsBlock(itemsHTML, isPreBill);
    const middleBlock = createMiddleBlock({
      isPreBill,
      orderInfoHTML,
      itemsBlock,
      totalsHTML,
      paymentHTML,
      footerHTML
    });

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${isPreBill ? 'Pre-Cuenta' : 'Ticket'} - Orden #${order.order_number}</title>
  <style>
    ${styles}
  </style>
</head>
<body class="${sizeClass}">
  ${headerHTML}
  ${middleBlock}
</body>
</html>
    `;
  };

  const buildPreBillHTML = (order: CustomerPrintOrder, paperWidth: number): string =>
    buildCustomerDocumentHTML(order, paperWidth, true);

  const buildReceiptHTML = (order: CustomerPrintOrder, paperWidth: number): string =>
    buildCustomerDocumentHTML(order, paperWidth, false);


  /**
   * Print a customer receipt for an order
   * Uses silent printing in Electron, browser print dialog otherwise
   */
  const printCustomerReceipt = async (order: CustomerPrintOrder) => {
    await printWithBuilder(
      order,
      buildReceiptHTML,
      {
        enabledSettingKey: 'customer_print_enabled',
        paperWidthKey: 'customer_print_paper_width',
        printerNameKey: 'customer_printer_name'
      },
      'Customer receipt'
    );
  };

  /**
   * Print a pre-bill (cuenta) for an order
   * Uses silent printing in Electron, browser print dialog otherwise
   * Used when customer asks for the bill before paying
   */
  const printPreBill = async (order: CustomerPrintOrder) => {
    await printWithBuilder(
      order,
      buildPreBillHTML,
      {
        enabledSettingKey: 'customer_print_enabled',
        paperWidthKey: 'customer_print_paper_width',
        printerNameKey: 'customer_printer_name'
      },
      'Pre-bill'
    );
  };

  return {
    isPrinting,
    printPreBill,
    printCustomerReceipt,
    electronPrint
  };
}
